import { useCallback, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import type { Chat } from 'api-client';
import { useAppContext } from '@/context';
import { profileFailure, useProfileResource } from '@/users/components/auth/profileRequests';

export default function ChatHome() {
  const { apiClients, user } = useAppContext();
  const navigate = useNavigate();
  const load = useCallback(async () => {
    const chats: Chat[] = [];
    let page = 1;
    let next: string | null | undefined = 'first';
    while (next) {
      const result = await apiClients.chat.chatsList({ page });
      chats.push(...result.results);
      next = result.next;
      page += 1;
    }
    return chats;
  }, [apiClients.chat]);
  const resource = useProfileResource(load, 'Could not load chats. Please retry.');
  const [pending, setPending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const create = async () => {
    if (pending) return;
    setPending(true);
    setError(null);
    const current = resource.generation.current;
    try {
      const chat = await apiClients.chat.chatsCreate({ chatWrite: {} });
      if (current === resource.generation.current) navigate(`/chat/${chat.id}`);
    } catch (failure) {
      const message = await profileFailure(failure, 'Could not create a chat. Please retry.');
      if (current === resource.generation.current) setError(message);
    } finally {
      if (current === resource.generation.current) setPending(false);
    }
  };
  return (
    <Stack spacing={3}>
      <Stack direction="row" useFlexGap flexWrap="wrap" gap={2} justifyContent="space-between">
        <Typography variant="h4" component="h1">
          AI Chats
        </Typography>
        <Button
          variant="contained"
          disabled={pending}
          loading={pending}
          loadingPosition="start"
          onClick={() => {
            void create();
          }}
        >
          {pending ? 'Creating chat…' : 'New Chat'}
        </Button>
      </Stack>
      {resource.loading && <CircularProgress aria-label="Loading chats" />}
      {(resource.error || error) && (
        <Alert
          severity="error"
          action={
            resource.error ? (
              <Button color="inherit" onClick={resource.retry}>
                Retry
              </Button>
            ) : undefined
          }
        >
          {resource.error || error}
        </Alert>
      )}
      {resource.data?.length === 0 && <Alert severity="info">No chats yet. Start a new chat.</Alert>}
      {resource.data?.map((chat) => (
        <Paper key={chat.id} sx={{ p: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ sm: 'center' }}
          >
            <Box>
              <Typography variant="h6" component="h2">
                {chat.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Updated {chat.updatedAt.toLocaleString(undefined, { timeZone: user?.timezone || undefined })}
              </Typography>
            </Box>
            <Button component={RouterLink} to={`/chat/${chat.id}`} variant="outlined">
              Open Chat
            </Button>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}
